/**
 * eventos.ts — cómo se hablan las dos raíces de React.
 *
 * Desde el 2026-09-28 el portal monta **dos** raíces: el cromo —menú, cabecera,
 * campana, aviso de ficha— y la pantalla que el router pone en
 * `#page-container`. Se montan por separado a propósito: es lo que permitió
 * migrar el armazón sin migrar el router.
 *
 * El precio es que **no comparten caché**. `Providers` crea su `QueryClient` con
 * `useState`, uno por raíz, y eso es deliberado (ver su cabecera): así volver a
 * una pantalla no reusa la caché de la anterior. Consecuencia: un
 * `invalidateQueries` de la pantalla **no llega al cromo**. Tampoco hay contexto
 * común, porque no hay un árbol común.
 *
 * Lo que sí cruza es un evento del `window`. Y el nombre se declara aquí, una
 * vez, para las dos partes:
 *
 * ```ts
 * window.dispatchEvent(new CustomEvent(EVENTO_FICHA));   // la pantalla avisa
 * window.addEventListener(EVENTO_FICHA, releer);          // el cromo escucha
 * ```
 *
 * ## Por qué un evento y no una llamada
 *
 * Porque ya se probó lo otro. El aviso de datos pendientes se repintaba con
 * `window.APP?.avisoFichaPendiente?.()`, y los favoritos con
 * `window.PageDashboard?.refreshQuickAccess?.()`. Los dos con `?.` delante: el
 * día que el otro lado dejó de existir **no falló nada**. Se guardaba bien y la
 * pantalla se quedaba como estaba, que es el defecto más caro de encontrar que
 * tiene este portal.
 *
 * Un evento no tiene ese problema: nadie tiene que existir para emitirlo, y
 * quien escuche se entera sin que el emisor sepa quién es. Y el nombre, al ser
 * un import, no se puede escribir mal en un solo lado.
 */

/** El router acabó de navegar. Lo emite `app.js`, lo escucha el cromo para
 *  marcar el destino activo y poner el título. */
export const EVENTO_NAVEGO = 'portal:navego';

/** Cambió lo que falta en la ficha de quien mira. Lo emite la pantalla de Perfil
 *  al guardar, lo escucha el aviso del cromo — que está en la otra raíz y no se
 *  enteraría de su `invalidateQueries`. */
export const EVENTO_FICHA = 'portal:ficha';

/** Cambiaron los favoritos o el historial. Lo emite el menú lateral, lo escucha
 *  la tarjeta «Tus accesos» del Inicio. Ver `lib/accesos.ts`. */
export const EVENTO_ACCESOS = 'portal:accesos';
